import { type WhitelabelSiteCustomizableString } from '../../api'

export type DropdownOption = {
  value: string
  label: string
  description?: string
}

export type OnboardingFieldValue = string[] | DropdownOption[] | string | boolean | undefined | null

export interface AddressData {
  streetAddress: string
  apartmentNumber: string
  city: string
  state: string
  postalCode: string
  country: string
  approvalRequests: ApprovalRequestData[]
  approved: boolean
}

// partial props of Address
export interface AddressFormData {
  streetAddress: string
  apartmentNumber: string
  city: string
  state: string
  postalCode: string
  country: string
}

// partial props of Warehouse
export interface OnboardingRetailerProfileFormData {
  name: string
  country: string
  allowAlcohol: boolean
  logo: string
  categories?: string[]
  description?: string
  pricingPolicy?: string
  pricingPolicyText?: string
  updatedPricingPolicy?: string
  updatedPricingPolicyText?: string
  logoBackgroundColor?: string
}

export interface OnboardingStoreLocationFormData {
  locationCode: string
  name: string
  address: AddressFormData
}

export interface PartnerData {
  id: string
  name: string
}

export interface ApprovalRequestData {
  id: string
  approveableId: string
  approveableType: string
  status: string
  attributeName: string
  attributeValue: string
  attributeGroup?: string
  comment: string
  createdAt: string
  updatedAt: string
  secretRefId?: string
}

export interface WarehouseData {
  id: string
  externalId: string
  name: string
  retailerType: string
  country: string
  categories: string[]
  description: string
  allowAlcohol: boolean
  logo: string
  pricingPolicy: string
  pricingPolicyText: string
  certifiedDeliveryThreshold: number
  logoBackgroundColor: string
  franchiseeId: string | undefined
  approvalRequests: ApprovalRequestData[] | undefined
  approved: boolean
  createdAt: string
  updatedAt: string
}

export interface WarehouseLocationData {
  id: string
  externalId: string
  locationCode: string
  name: string
  address: AddressData
  approvalRequests: ApprovalRequestData[] | undefined
  approvalRequestsStatus: string | null
  approved: boolean
  updatedAt: string
  createdAt: string
}

export interface WarehouseLocationTableData {
  id: string
  externalId: string
  locationCode: string
  name: string
  address: AddressData
  approvalRequests: ApprovalRequestData[] | undefined
  approvalRequestsStatus: string
  approved: boolean
  active: boolean
  storeHoursCompleted: boolean
  updatedAt: string
  createdAt: string
}

export interface LaunchOnboardingData {
  id: string
  entityName: string
  workflowState: string
  partner: PartnerData
  warehouse: WarehouseData
  warehouseLocations: WarehouseLocationData[]
}

export interface PaginationMetadata {
  page: string
  perPage: string
  totalPages: string
  totalCount: string
}

export enum WhitelabelSiteWorkflowState {
  initialized = 'initialized',
  initialEmailSent = 'initial_email_sent',
  inProgress = 'in_progress',
  inReview = 'in_review',
  published = 'published',
}

export interface WhitelabelSiteData {
  id: string
  tier: string
  warehouseId: string
  launchOnboardingWarehouseId: string
  storeConfigurationId: string
  type: string
  partnerId: string
  ownerUserUuid?: string
  retailerUserUuid?: string
  workflowState: WhitelabelSiteWorkflowState
  approvalRequests?: ApprovalRequestData[]
  updatedAt: string
  createdAt: string
  deletedAt: string | null
}

export interface StoreConfigurationData {
  id: string
  slug: string
  brandedName: string
  whitelistedWarehouseIds: string[]
  domainName: string
  deApplicationPlatform: string
  createdAt: string
  updatedAt: string
  allowAllWarehouses: boolean
  logoUrl: string
  active: boolean
  userIsolationContextId: string
  partnerAccessSettings: {
    visibleToPartnersOnIpp: boolean
    modificationRules: {
      allowlistPartnerIds: string[]
      allowlistRetailerIds: string[]
    }
    optionalVisibleToPartnersOnIpp: boolean
  }
}

export interface SiteCustomizableStringData
  extends Omit<WhitelabelSiteCustomizableString, '__typename'> {}

export interface LocaleStringsData {
  id: number
  value: string
  customValue?: string
  valuePath: string
  sourcePath: string
  githubLink?: string
}

export interface MobileDashboardData {
  id: string
  name: string
  whitelabelSiteId: string
  storeConfigurationId: string
  appId: string
  platform: MobileAppPlatform
  slug: string
  currentReleaseVersion: string
  currentReleaseDate: string
  inProgressVersion: string
  inProgressDate: string
  inProgressStatus: MobileAppStatus
  betaReleaseVersion: string
  betaReleaseDate: string
  betaReleaseStatus: MobileAppStatus
  createdAt: string
  createdByUuid: string
  updatedAt: string
  updatedByUuid: string
  deletedAt: string
}

export enum MobileAppStatus {
  APP_STATUS_UNKNOWN = 'APP_STATUS_UNKNOWN',
  APP_STATUS_AVAILABLE = 'APP_STATUS_AVAILABLE',
  APP_STATUS_APPROVED = 'APP_STATUS_APPROVED',
  APP_STATUS_REJECTED = 'APP_STATUS_REJECTED',
  APP_STATUS_SUBMITTED = 'APP_STATUS_SUBMITTED',
}

export enum MobileAppPlatform {
  ANDROID = 'android',
  IOS = 'ios',
}
